import { useCallback, useEffect, useMemo, useState } from "react";
import {
  DashboardImageUploadSchema,
  DashboardMenuSchema,
  DashboardRestaurantListSchema,
  type DashboardMenu,
  type DashboardMenuCategory,
  type DashboardMenuItem,
  type DashboardRestaurant,
} from "@bytebite/schemas";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { apiDelete, apiGet, apiPatch, apiPost, apiPut, apiUpload } from "@/lib/api";
import { centsToEuroInput, eurosToCents, formatEuro } from "@/lib/money";
import { readStoredRestaurantId, storeRestaurantId } from "@/lib/restaurant-context";

const fieldClass =
  "border-input bg-background w-full rounded-md border px-3 py-2 text-sm shadow-sm";

export function MenuPage() {
  const [restaurants, setRestaurants] = useState<DashboardRestaurant[]>([]);
  const [restaurantId, setRestaurantId] = useState<string | null>(null);
  const [menu, setMenu] = useState<DashboardMenu | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const selected = useMemo(
    () => restaurants.find((restaurant) => restaurant.id === restaurantId) ?? null,
    [restaurantId, restaurants],
  );

  const loadMenu = useCallback(async (id: string) => {
    const payload = DashboardMenuSchema.parse(
      await apiGet<unknown>(`/dashboard/restaurants/${id}/menu`),
    );
    setMenu(payload);
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function boot() {
      try {
        const list = DashboardRestaurantListSchema.parse(
          await apiGet<unknown>("/dashboard/restaurants"),
        );
        if (cancelled) return;
        setRestaurants(list.restaurants);
        const stored = readStoredRestaurantId(list.restaurants[0]?.id ?? null);
        const next =
          list.restaurants.find((restaurant) => restaurant.id === stored)?.id ??
          list.restaurants[0]?.id ??
          null;
        setRestaurantId(next);
        if (next) {
          storeRestaurantId(next);
          await loadMenu(next);
        }
      } catch {
        if (!cancelled) setError("Could not load restaurants.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void boot();
    return () => {
      cancelled = true;
    };
  }, [loadMenu]);

  async function changeRestaurant(id: string) {
    setRestaurantId(id);
    storeRestaurantId(id);
    setError(null);
    try {
      await loadMenu(id);
    } catch {
      setError("Could not load this menu.");
    }
  }

  if (loading) {
    return <p className="text-muted-foreground text-sm">Loading menu…</p>;
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Menu</h1>
          <p className="text-muted-foreground text-sm">
            Categories and products for the selected restaurant. Path context stays the tenant.
          </p>
        </div>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-muted-foreground">Restaurant</span>
          <select
            className={fieldClass}
            value={restaurantId ?? ""}
            onChange={(event) => void changeRestaurant(event.target.value)}
          >
            {restaurants.map((restaurant) => (
              <option key={restaurant.id} value={restaurant.id}>
                {restaurant.name}
              </option>
            ))}
          </select>
        </label>
      </div>
      {error ? <p className="text-destructive text-sm">{error}</p> : null}
      {selected && restaurantId && menu ? (
        <MenuEditor
          restaurantId={restaurantId}
          menu={menu}
          onReload={() => loadMenu(restaurantId)}
        />
      ) : (
        <p className="text-muted-foreground text-sm">No restaurants available.</p>
      )}
    </div>
  );
}

function MenuEditor({
  restaurantId,
  menu,
  onReload,
}: {
  restaurantId: string;
  menu: DashboardMenu;
  onReload: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  async function run(action: () => Promise<void>) {
    setBusy(true);
    setMessage(null);
    try {
      await action();
      await onReload();
    } catch {
      setMessage("The change could not be saved.");
    } finally {
      setBusy(false);
    }
  }

  async function moveCategory(index: number, direction: -1 | 1) {
    const next = index + direction;
    if (next < 0 || next >= menu.categories.length) return;
    const ids = menu.categories.map((category) => category.id);
    const swap = ids[index]!;
    ids[index] = ids[next]!;
    ids[next] = swap;
    await run(() => apiPut(`/dashboard/restaurants/${restaurantId}/categories/order`, { ids }));
  }

  async function moveItem(category: DashboardMenuCategory, index: number, direction: -1 | 1) {
    const next = index + direction;
    if (next < 0 || next >= category.items.length) return;
    const ids = category.items.map((item) => item.id);
    const swap = ids[index]!;
    ids[index] = ids[next]!;
    ids[next] = swap;
    await run(() =>
      apiPut(`/dashboard/restaurants/${restaurantId}/items/order`, {
        categoryId: category.id,
        ids,
      }),
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {message ? <p className="text-destructive text-sm">{message}</p> : null}
      <CategoryForm
        disabled={busy}
        onCreate={(body) =>
          run(() => apiPost(`/dashboard/restaurants/${restaurantId}/categories`, body))
        }
      />
      {menu.categories.length === 0 ? (
        <p className="text-muted-foreground text-sm">No categories yet.</p>
      ) : null}
      {menu.categories.map((category, index) => (
        <Card key={category.id}>
          <CardHeader className="gap-3">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <CardTitle>{category.name}</CardTitle>
                <CardDescription>
                  {category.isActive ? "Active" : "Inactive"}
                  {category.description ? ` · ${category.description}` : ""}
                </CardDescription>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={busy || index === 0}
                  onClick={() => void moveCategory(index, -1)}
                >
                  Up
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={busy || index === menu.categories.length - 1}
                  onClick={() => void moveCategory(index, 1)}
                >
                  Down
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={busy}
                  onClick={() =>
                    void run(() =>
                      apiPatch(`/dashboard/restaurants/${restaurantId}/categories/${category.id}`, {
                        isActive: !category.isActive,
                      }),
                    )
                  }
                >
                  {category.isActive ? "Deactivate" : "Activate"}
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  disabled={busy}
                  onClick={() => {
                    if (
                      !window.confirm(
                        `Delete category “${category.name}” and its products? This cannot be undone.`,
                      )
                    ) {
                      return;
                    }
                    void run(() =>
                      apiDelete(`/dashboard/restaurants/${restaurantId}/categories/${category.id}`),
                    );
                  }}
                >
                  Delete
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <CategoryEditForm
              category={category}
              disabled={busy}
              onSave={(body) =>
                run(() =>
                  apiPatch(
                    `/dashboard/restaurants/${restaurantId}/categories/${category.id}`,
                    body,
                  ),
                )
              }
              onUpload={(file) =>
                run(async () => {
                  DashboardImageUploadSchema.parse(
                    await apiUpload(
                      `/dashboard/restaurants/${restaurantId}/categories/${category.id}/image`,
                      file,
                    ),
                  );
                })
              }
            />
            {category.items.map((item, itemIndex) => (
              <ItemRow
                key={item.id}
                item={item}
                disabled={busy}
                isFirst={itemIndex === 0}
                isLast={itemIndex === category.items.length - 1}
                onMove={(direction) => void moveItem(category, itemIndex, direction)}
                onToggle={() =>
                  void run(() =>
                    apiPatch(`/dashboard/restaurants/${restaurantId}/items/${item.id}`, {
                      isActive: !item.isActive,
                    }),
                  )
                }
                onSave={(body) =>
                  run(() =>
                    apiPatch(`/dashboard/restaurants/${restaurantId}/items/${item.id}`, body),
                  )
                }
                onSaveVariants={(body) =>
                  run(() =>
                    apiPut(
                      `/dashboard/restaurants/${restaurantId}/items/${item.id}/variants`,
                      body,
                    ),
                  )
                }
                onUpload={(file) =>
                  run(async () => {
                    DashboardImageUploadSchema.parse(
                      await apiUpload(
                        `/dashboard/restaurants/${restaurantId}/items/${item.id}/image`,
                        file,
                      ),
                    );
                  })
                }
                onDelete={() => {
                  if (!window.confirm(`Delete “${item.name}”?`)) return;
                  void run(() =>
                    apiDelete(`/dashboard/restaurants/${restaurantId}/items/${item.id}`),
                  );
                }}
              />
            ))}
            <ItemForm
              disabled={busy}
              onCreate={(body) =>
                run(() =>
                  apiPost(`/dashboard/restaurants/${restaurantId}/items`, {
                    ...body,
                    categoryId: category.id,
                  }),
                )
              }
            />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function CategoryForm({
  disabled,
  onCreate,
}: {
  disabled: boolean;
  onCreate: (body: { name: string; description: string | null }) => void;
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  return (
    <Card>
      <CardHeader>
        <CardTitle>Add category</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <label className="flex flex-1 flex-col gap-1 text-sm">
          Name
          <input
            className={fieldClass}
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </label>
        <label className="flex flex-1 flex-col gap-1 text-sm">
          Description
          <input
            className={fieldClass}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </label>
        <Button
          type="button"
          disabled={disabled || name.trim().length === 0}
          onClick={() => {
            onCreate({ name: name.trim(), description: description.trim() || null });
            setName("");
            setDescription("");
          }}
        >
          Add
        </Button>
      </CardContent>
    </Card>
  );
}

function ImageFileInput({
  disabled,
  onUpload,
}: {
  disabled: boolean;
  onUpload: (file: File) => void;
}) {
  return (
    <input
      type="file"
      accept="image/jpeg,image/png,image/webp,image/gif"
      className={fieldClass}
      disabled={disabled}
      onChange={(event) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (file) onUpload(file);
      }}
    />
  );
}

function CategoryEditForm({
  category,
  disabled,
  onSave,
  onUpload,
}: {
  category: DashboardMenuCategory;
  disabled: boolean;
  onSave: (body: { name: string; description: string | null; imageUrl: string | null }) => void;
  onUpload: (file: File) => void;
}) {
  const [name, setName] = useState(category.name);
  const [description, setDescription] = useState(category.description ?? "");
  const [imageUrl, setImageUrl] = useState(category.imageUrl ?? "");

  useEffect(() => {
    setName(category.name);
    setDescription(category.description ?? "");
    setImageUrl(category.imageUrl ?? "");
  }, [category.description, category.imageUrl, category.name]);

  return (
    <div className="flex flex-col gap-3">
      {category.imageUrl ? (
        <img
          src={category.imageUrl}
          alt=""
          className="h-24 w-full max-w-xs rounded-md object-cover"
        />
      ) : null}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <input
          className={fieldClass}
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
        <input
          className={fieldClass}
          value={description}
          placeholder="Description"
          onChange={(event) => setDescription(event.target.value)}
        />
        <div className="flex gap-2">
          <input
            className={fieldClass}
            value={imageUrl}
            placeholder="Image URL"
            onChange={(event) => setImageUrl(event.target.value)}
          />
          <Button
            type="button"
            variant="secondary"
            disabled={disabled || name.trim().length === 0}
            onClick={() =>
              onSave({
                name: name.trim(),
                description: description.trim() || null,
                imageUrl: imageUrl.trim() || null,
              })
            }
          >
            Save
          </Button>
        </div>
      </div>
      <ImageFileInput disabled={disabled} onUpload={onUpload} />
    </div>
  );
}

function ItemForm({
  disabled,
  onCreate,
}: {
  disabled: boolean;
  onCreate: (body: {
    name: string;
    description: string | null;
    priceCents: number;
    imageUrl: string | null;
  }) => void;
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [imageUrl, setImageUrl] = useState("");

  return (
    <div className="grid grid-cols-1 gap-3 rounded-lg border p-3 md:grid-cols-5">
      <input
        className={fieldClass}
        placeholder="New product"
        value={name}
        onChange={(event) => setName(event.target.value)}
      />
      <input
        className={fieldClass}
        placeholder="Description"
        value={description}
        onChange={(event) => setDescription(event.target.value)}
      />
      <input
        className={fieldClass}
        placeholder="Price (€)"
        value={price}
        onChange={(event) => setPrice(event.target.value)}
      />
      <input
        className={fieldClass}
        placeholder="Image URL"
        value={imageUrl}
        onChange={(event) => setImageUrl(event.target.value)}
      />
      <Button
        type="button"
        disabled={disabled || name.trim().length === 0 || eurosToCents(price) === null}
        onClick={() => {
          const priceCents = eurosToCents(price);
          if (priceCents === null) return;
          onCreate({
            name: name.trim(),
            description: description.trim() || null,
            priceCents,
            imageUrl: imageUrl.trim() || null,
          });
          setName("");
          setDescription("");
          setPrice("");
          setImageUrl("");
        }}
      >
        Add product
      </Button>
    </div>
  );
}

type SizeDraft = {
  key: string;
  id?: string;
  name: string;
  price: string;
  isActive: boolean;
};

function ItemVariants({
  item,
  disabled,
  onSave,
}: {
  item: DashboardMenuItem;
  disabled: boolean;
  onSave: (body: {
    variants: {
      id?: string;
      name: string;
      priceCents: number;
      isActive: boolean;
      sortOrder: number;
    }[];
  }) => void;
}) {
  const [formError, setFormError] = useState<string | null>(null);
  const [rows, setRows] = useState<SizeDraft[]>(() =>
    item.variants.map((variant) => ({
      key: variant.id,
      id: variant.id,
      name: variant.name,
      price: centsToEuroInput(variant.priceCents),
      isActive: variant.isActive,
    })),
  );

  useEffect(() => {
    setRows(
      item.variants.map((variant) => ({
        key: variant.id,
        id: variant.id,
        name: variant.name,
        price: centsToEuroInput(variant.priceCents),
        isActive: variant.isActive,
      })),
    );
  }, [item.variants]);

  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm font-medium">Sizes</p>
      <p className="text-muted-foreground text-xs">
        Leave this empty to sell the product at its own price. An active size replaces that price.
      </p>
      {rows.map((row, index) => (
        <div key={row.key} className="grid grid-cols-1 items-center gap-2 md:grid-cols-4">
          <input
            className={fieldClass}
            value={row.name}
            placeholder="26 cm"
            onChange={(event) =>
              setRows((current) =>
                current.map((entry) =>
                  entry.key === row.key ? { ...entry, name: event.target.value } : entry,
                ),
              )
            }
          />
          <input
            className={fieldClass}
            value={row.price}
            placeholder="10,00"
            onChange={(event) =>
              setRows((current) =>
                current.map((entry) =>
                  entry.key === row.key ? { ...entry, price: event.target.value } : entry,
                ),
              )
            }
          />
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={row.isActive}
              onChange={(event) =>
                setRows((current) =>
                  current.map((entry) =>
                    entry.key === row.key ? { ...entry, isActive: event.target.checked } : entry,
                  ),
                )
              }
            />
            Active
          </label>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={disabled || index === 0}
              onClick={() =>
                setRows((current) => {
                  const next = current.slice();
                  const previous = next[index - 1];
                  const currentRow = next[index];
                  if (!previous || !currentRow) return current;
                  next[index - 1] = currentRow;
                  next[index] = previous;
                  return next;
                })
              }
            >
              Up
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={disabled || index === rows.length - 1}
              onClick={() =>
                setRows((current) => {
                  const next = current.slice();
                  const following = next[index + 1];
                  const currentRow = next[index];
                  if (!following || !currentRow) return current;
                  next[index + 1] = currentRow;
                  next[index] = following;
                  return next;
                })
              }
            >
              Down
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={disabled}
              onClick={() => setRows((current) => current.filter((entry) => entry.key !== row.key))}
            >
              Remove
            </Button>
          </div>
        </div>
      ))}
      {formError ? <p className="text-destructive text-sm">{formError}</p> : null}
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={() =>
            setRows((current) => [
              ...current,
              { key: `new-${current.length}-${Date.now()}`, name: "", price: "", isActive: true },
            ])
          }
        >
          Add size
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={disabled}
          onClick={() => {
            const variants = [];
            for (const [index, row] of rows.entries()) {
              const priceCents = eurosToCents(row.price);
              if (priceCents === null || row.name.trim().length === 0) {
                setFormError("Each size needs a name and a price like 10,00.");
                return;
              }
              variants.push({
                ...(row.id ? { id: row.id } : {}),
                name: row.name.trim(),
                priceCents,
                isActive: row.isActive,
                sortOrder: index,
              });
            }
            setFormError(null);
            onSave({ variants });
          }}
        >
          Save sizes
        </Button>
      </div>
    </div>
  );
}

function ItemRow({
  item,
  disabled,
  isFirst,
  isLast,
  onMove,
  onToggle,
  onSave,
  onSaveVariants,
  onUpload,
  onDelete,
}: {
  item: DashboardMenuItem;
  disabled: boolean;
  isFirst: boolean;
  isLast: boolean;
  onMove: (direction: -1 | 1) => void;
  onToggle: () => void;
  onSave: (body: {
    name: string;
    description: string | null;
    priceCents: number;
    imageUrl: string | null;
  }) => void;
  onSaveVariants: (body: {
    variants: {
      id?: string;
      name: string;
      priceCents: number;
      isActive: boolean;
      sortOrder: number;
    }[];
  }) => void;
  onUpload: (file: File) => void;
  onDelete: () => void;
}) {
  const [name, setName] = useState(item.name);
  const [description, setDescription] = useState(item.description ?? "");
  const [price, setPrice] = useState(centsToEuroInput(item.priceCents));
  const [imageUrl, setImageUrl] = useState(item.imageUrl ?? "");

  useEffect(() => {
    setName(item.name);
    setDescription(item.description ?? "");
    setPrice(centsToEuroInput(item.priceCents));
    setImageUrl(item.imageUrl ?? "");
  }, [item.description, item.imageUrl, item.name, item.priceCents]);

  const priceCents = eurosToCents(price);

  return (
    <div className="flex flex-col gap-3 rounded-lg border p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-medium">
          {item.name} · {formatEuro(item.priceCents)} · {item.isActive ? "Active" : "Inactive"}
        </p>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled || isFirst}
            onClick={() => onMove(-1)}
          >
            Up
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled || isLast}
            onClick={() => onMove(1)}
          >
            Down
          </Button>
          <Button type="button" variant="outline" size="sm" disabled={disabled} onClick={onToggle}>
            {item.isActive ? "Deactivate" : "Activate"}
          </Button>
          <Button
            type="button"
            variant="destructive"
            size="sm"
            disabled={disabled}
            onClick={onDelete}
          >
            Delete
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-5">
        <input
          className={fieldClass}
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
        <input
          className={fieldClass}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
        <input
          className={fieldClass}
          value={price}
          onChange={(event) => setPrice(event.target.value)}
        />
        <input
          className={fieldClass}
          value={imageUrl}
          placeholder="Image URL"
          onChange={(event) => setImageUrl(event.target.value)}
        />
        <Button
          type="button"
          variant="secondary"
          disabled={disabled || name.trim().length === 0 || priceCents === null}
          onClick={() => {
            if (priceCents === null) return;
            onSave({
              name: name.trim(),
              description: description.trim() || null,
              priceCents,
              imageUrl: imageUrl.trim() || null,
            });
          }}
        >
          Save
        </Button>
      </div>
      <ItemVariants item={item} disabled={disabled} onSave={onSaveVariants} />
      {item.imageUrl ? (
        <img src={item.imageUrl} alt="" className="h-24 w-full max-w-xs rounded-md object-cover" />
      ) : null}
      <ImageFileInput disabled={disabled} onUpload={onUpload} />
    </div>
  );
}
